// src/components/global/Table/ResourceTableHeader.jsx
import React from 'react';
import { Box, Stack, Typography, TextField, Button } from '@mui/material';
import { Add, Search, Archive, ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const TableHeader = ({
    title,
    subtitle,
    search = '',
    onSearchChange,
    addButtonLabel,
    onAddClick,
    addButtonVisible = true,
    addButtonDisabled = false,
    addButtonTooltip,
    headerExtra,

    // Archive Toggle
    archiveButtonVisible = true,
    isArchiveView = false,
    onArchiveToggle,
}) => {
    const theme = useTheme();
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                px: 2.5,
                py: 2.5,
                position: 'relative',
                overflow: 'hidden',
                bgcolor: theme.palette.primary.main,
                backgroundImage: isDark
                    ? `radial-gradient(circle at top left, rgba(56,189,248,0.35), transparent 55%),
         linear-gradient(135deg, ${theme.palette.primary.main} 0%, #0F172A 100%)`
                    : `radial-gradient(circle at top left, rgba(129,212,250,0.55), transparent 55%),
         linear-gradient(135deg, ${theme.palette.primary.main} 0%, #90CAF9 100%)`,
                color: '#FFFFFF',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.12,
                    backgroundImage:
                        'radial-gradient(circle at 20% 0, #FFFFFF, transparent 60%)',
                    pointerEvents: 'none',
                }}
            />

            <Stack
                spacing={2}
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                sx={{ position: 'relative', zIndex: 1 }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={700}>
                        {typeof title === 'object' ? (isArabic ? title.ar : title.en) : title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {typeof subtitle === 'object' ? (isArabic ? subtitle.ar : subtitle.en) : subtitle}
                        </Typography>
                    )}
                </Box>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{ width: { xs: '100%', sm: 'auto' }, gap: 2 }}
                >
                    <TextField
                        size="small"
                        variant="outlined"
                        value={search}
                        onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                        placeholder={isArabic ? 'ابحث في القائمة...' : 'Search...'}
                        sx={{
                            minWidth: 260,
                            width: { xs: '100%', sm: 'auto' },
                            bgcolor: '#FFFFFF',
                            borderRadius: 3,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                color: '#000000',
                                '& fieldset': {
                                    borderColor: 'rgba(15,23,42,0.18)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(15,23,42,0.35)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: theme.palette.primary.main,
                                    borderWidth: 1.3,
                                },
                            },
                        }}
                        InputLabelProps={{ shrink: false }}
                        InputProps={{
                            startAdornment: (
                                <Box
                                    sx={{
                                        pr: 1,
                                        pl: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: '#64748B',
                                    }}
                                >
                                    <Search fontSize="small" />
                                </Box>
                            ),
                            inputProps: { 'aria-label': isArabic ? 'بحث' : 'Search' },
                        }}
                    />

                    {headerExtra}

                    {/* Archive Toggle Button */}
                    {archiveButtonVisible && (
                        <Button
                            variant="outlined"
                            startIcon={isArchiveView ? <ArrowBack /> : <Archive />}
                            onClick={onArchiveToggle}
                            sx={{
                                whiteSpace: 'nowrap',
                                fontWeight: 600,
                                borderRadius: 2.5,
                                px: 2.8,
                                color: '#FFFFFF',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: '#FFFFFF',
                                    bgcolor: 'rgba(255,255,255,0.1)',
                                },
                            }}
                        >
                            {isArchiveView
                                ? (isArabic ? 'العودة للقائمة' : 'Back to List')
                                : (isArabic ? 'الأرشيف' : 'Archive')
                            }
                        </Button>
                    )}

                    {addButtonVisible && (
                        <Button
                            variant="contained"
                            color="secondary" // Changed to secondary for contrast if primary is header bg
                            startIcon={<Add />}
                            onClick={onAddClick}
                            disabled={addButtonDisabled}
                            title={addButtonTooltip}
                            sx={{
                                whiteSpace: 'nowrap',
                                fontWeight: 600,
                                borderRadius: 2.5,
                                px: 2.8,
                                bgcolor: isDark ? 'secondary.main' : '#FFFFFF',
                                color: isDark ? '#FFFFFF' : theme.palette.primary.main,
                                '&:hover': {
                                    bgcolor: isDark ? 'secondary.dark' : 'rgba(255,255,255,0.9)',
                                }
                            }}
                        >
                            {typeof addButtonLabel === 'object' ? (isArabic ? addButtonLabel.ar : addButtonLabel.en) : addButtonLabel}
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default TableHeader;
